/**
 * BidTablePrimitives.jsx
 *
 * Shared building blocks for the two bid-tracking-style tables:
 *   - ProjectsView's bid tracking table (dense px-3 padding, amber "Leftover"
 *     header support)
 *   - OpportunitiesTable (compact px-1.5 padding)
 *
 * Extracted verbatim from those files so each table renders byte-identically:
 *   - Th        - bold uppercase header cell on the pale-green band with the
 *                 funnel filter button; `variant` picks the per-table class /
 *                 style composition ('compact' default, 'projects' for the
 *                 ProjectsView bid table). `sticky` pins the cell to the top
 *                 of the table's scroll pane (see STICKY_* notes below).
 *                 Customizable tables (10/9/26) also pass `width` (the
 *                 person's px), `onResize(px)` / `onResizeReset()` for the
 *                 grip on the right edge, and `drag` (dragstart / dragover /
 *                 drop handlers) so the header can be dragged to reorder,
 *                 and `fontSize` (px) for the person's header size; round 4
 *                 adds `pinLeft` (a frozen column's left, px) and `align`.
 *   - Td        - data cell; `pad` carries the per-table padding classes.
 *   - shortDate - compact M/D/YY (e.g. 5/13/26) - saves ~3 chars vs M/D/YYYY
 *                 so wide bid-tracking rows fit on a 1280px laptop without
 *                 h-scroll. Full M/D/YYYY still appears in detail panels.
 *   - renderFilterPopover - plain render helper (NOT a component) that picks
 *                 the date / numeric / value-list popover for a column. All
 *                 per-table state comes in as explicit args so behavior is
 *                 unchanged from the previous inline blocks.
 */

import { ColumnFilterPopover } from './ColumnFilterPopover';
import { DateFilterPopover } from './DateFilterPopover';
import { NumericFilterPopover } from './NumericFilterPopover';

export function shortDate(d) {
  if (!d) return null;
  if (typeof d === 'string') {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1].slice(2)}`;
  }
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date)) return null;
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`;
}

/* ── Sticky-header constants ──────────────────────────────────────────────
   A sticky <th> floats ABOVE the rows that scroll under it, so its background
   has to be opaque - the pale-green band is normally painted by a Tailwind
   class, and the filter-active tint (bg-gat-green/[.12] -> #2e7d321f) is
   translucent, which would let row text bleed through. STICKY_BG_FILTERED is
   that same tint pre-flattened over the card's white surface, so a filtered
   column looks identical to today.
   The 2px header underline also has to be re-drawn as an inset shadow: with
   `border-collapse: collapse` the collapsed border belongs to the table, not
   to the cell, so it does not travel with the cell once it is stuck. */
const STICKY_BG = '#E8EFE0'; // = bg-[#E8EFE0] header band
const STICKY_BG_FILTERED = '#E6EFE6'; // = #2e7d321f flattened over #FFFFFF
const STICKY_BG_AMBER = '#FEF3C7'; // = the amber "Leftover" header
const STICKY_UNDERLINE = '#1B5E20'; // = border-gat-greenDark
const STICKY_UNDERLINE_AMBER = '#92400E';
// Above the body cells (unpositioned) and above the pinned totals row (4).
const STICKY_Z = 5;

/**
 * The resize grip: drag the header's right edge (round 3, 10/9/26 - "make
 * the resize column work properly"). The whole column follows the pointer,
 * not just its header: each animation frame sets that column's <col> width
 * and the table's width, in the DOM (no re-render of a several-hundred-row
 * table per pixel). The first resize freezes the table at the widths on
 * screen (fixed layout), so a column can go narrower than its content - what
 * does not fit ends in an ellipsis (index.css .tv-fixed). When the pointer
 * comes up the width is handed to onResize(px, measured), `measured` being
 * every column's width as it was on screen ({ colId: px }), and React takes
 * over. A drag that changed nothing puts the DOM back. Double-click resets
 * the column.
 */
function ResizeGrip({ label, onResize, onResizeReset }) {
  const start = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const grip = e.currentTarget;
    const th = grip.closest('th');
    const table = th.closest('table');
    const ths = [...th.parentElement.children];
    const group = table ? [...table.children].find((c) => c.tagName === 'COLGROUP') : null;
    const cols = group ? [...group.children] : [];
    const idx = ths.indexOf(th);
    const col = cols[idx];
    const widthOf = (el) => Math.round(el.getBoundingClientRect().width);
    const measured = {};
    const startWidths = ths.map(widthOf);
    ths.forEach((h, i) => {
      const id = h.getAttribute('data-col');
      if (id && id !== 'actions') measured[id] = startWidths[i];
    });
    const w0 = startWidths[idx];
    // put back exactly what was there if the drag changes nothing
    const saved = {
      table: table ? { cls: table.className, style: table.getAttribute('style') } : null,
      cols: cols.map((c) => c.getAttribute('style')),
      th: th.getAttribute('style'),
    };
    if (table && !table.classList.contains('tv-fixed')) {
      // freeze: every column at its width on screen, then fixed layout
      cols.forEach((c, i) => {
        const w = startWidths[i];
        if (w) {
          c.style.width = `${w}px`;
          c.style.minWidth = `${w}px`;
          c.style.maxWidth = `${w}px`;
        }
      });
      table.classList.add('tv-fixed');
      table.style.minWidth = '0';
    }
    const others = startWidths.reduce((n, w, i) => (i === idx ? n : n + w), 0);
    let w = w0;
    let frame = 0;
    let done = false;
    const paint = () => {
      frame = 0;
      const px = `${w}px`;
      if (col) {
        col.style.width = px;
        col.style.minWidth = px;
        col.style.maxWidth = px;
      }
      th.style.width = px;
      th.style.minWidth = px;
      th.style.maxWidth = px;
      if (table) table.style.width = `${others + w}px`;
    };
    const move = (ev) => {
      w = Math.max(40, Math.min(600, Math.round(w0 + ev.clientX - e.clientX)));
      if (frame) return;
      if (typeof window.requestAnimationFrame === 'function') frame = window.requestAnimationFrame(paint);
      else paint();
    };
    const up = () => {
      if (done) return; // pointerup and mouseup both arrive
      done = true;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      if (frame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
      frame = 0;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      grip.removeAttribute('data-active');
      if (w !== w0) {
        paint();
        // React renders the fixed table from here; the drag-time min-width must
        // not outlive it (it would undo min-w-[1280px] after Reset widths)
        if (table) table.style.minWidth = '';
        onResize(w, measured);
        return;
      }
      // nothing changed: the DOM goes back to what React rendered
      if (saved.table) {
        table.className = saved.table.cls;
        if (saved.table.style == null) table.removeAttribute('style');
        else table.setAttribute('style', saved.table.style);
      }
      cols.forEach((c, i) => {
        if (saved.cols[i] == null) c.removeAttribute('style');
        else c.setAttribute('style', saved.cols[i]);
      });
      if (saved.th == null) th.removeAttribute('style');
      else th.setAttribute('style', saved.th);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    grip.setAttribute('data-active', '');
    // pointer events, with mouse events as the fallback (older test DOMs)
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      title="Drag to resize · double-click to reset"
      className="col-resize-grip"
      onPointerDown={start}
      onMouseDown={(e) => {
        if (typeof window.PointerEvent !== 'function') start(e);
        else e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onResizeReset?.();
      }}
      draggable={false}
      style={{ position: 'absolute', top: 0, right: 0, width: 8, height: '100%', cursor: 'col-resize', zIndex: 1, touchAction: 'none' }}
    />
  );
}

export function Th({
  col,
  onSort,
  num,
  children,
  filterable,
  filterActive,
  onFilterClick,
  className = '',
  variant = 'compact',
  sticky = false,
  stickyTop = 0,
  width,
  onResize,
  onResizeReset,
  resizeLabel,
  drag,
  fontSize,
  pinLeft,
  align,
}) {
  const sortable = !!onSort;
  const isAmber = className.startsWith('!bg-amber-50');
  let thClassName;
  let thStyle;
  if (variant === 'projects') {
    // ProjectsView bid-tracking header - px-3 py-2.5 + amber override support.
    thClassName = [
      'text-left px-3 py-2.5 uppercase whitespace-nowrap relative border-b-2',
      num ? 'text-right' : '',
      filterActive ? 'bg-gat-green/[.12]' : isAmber ? '' : 'bg-[#E8EFE0]',
      isAmber ? '' : 'border-gat-greenDark',
      className.replace(/^!/, ''),
    ].join(' ');
    thStyle = {
      ...(isAmber ? { background: '#FEF3C7', color: '#92400E', borderBottom: '2px solid #92400E' } : {}),
      letterSpacing: '0.5px',
      fontSize: '9.5px',
      fontWeight: 800,
      color: isAmber ? '#92400E' : '#0D0D0D',
    };
  } else {
    // OpportunitiesTable compact header - px-1.5 py-1.5.
    thClassName = [
      'text-left px-1.5 py-1.5 uppercase whitespace-nowrap relative border-b-2 bg-[#E8EFE0] border-gat-greenDark',
      num ? 'text-right' : '',
      filterActive ? 'bg-gat-green/[.12]' : '',
      className,
    ].join(' ');
    thStyle = { letterSpacing: '0.5px', fontSize: '9.5px', fontWeight: 800, color: '#0D0D0D' };
  }
  if (sticky) {
    thStyle = {
      ...thStyle,
      position: 'sticky',
      top: stickyTop,
      zIndex: STICKY_Z,
      background: isAmber ? STICKY_BG_AMBER : filterActive ? STICKY_BG_FILTERED : STICKY_BG,
      boxShadow: `inset 0 -2px 0 ${isAmber ? STICKY_UNDERLINE_AMBER : STICKY_UNDERLINE}`,
    };
  }
  if (width) thStyle = { ...thStyle, width, minWidth: width, maxWidth: width };
  if (fontSize) thStyle = { ...thStyle, fontSize }; // the person's size for this header (10/9/26)
  // a frozen column (round 4): sticks at the left too, above the other headers
  if (pinLeft != null)
    thStyle = { ...thStyle, position: 'sticky', left: pinLeft, zIndex: STICKY_Z + 1, ...(sticky ? {} : { background: STICKY_BG }) };
  if (onResize && !sticky && pinLeft == null) thStyle = { ...thStyle, position: 'relative' };
  return (
    <th className={thClassName} style={thStyle} data-col={col} {...(drag || {})}>
      <div
        className={[
          'flex items-center gap-1',
          (align ? align === 'right' : num) ? 'justify-end' : '',
          align === 'center' ? 'justify-center' : '',
        ].join(' ')}
      >
        {/* The filter popover is a child of this span, rendered through a portal
            at the end of <body>. React bubbles its clicks along the COMPONENT
            tree, so every checkbox / Apply / sort click inside it also re-sorted
            the table. Only sort when the click really landed in the header. */}
        <span
          onClick={
            sortable
              ? (e) => {
                  if (e.currentTarget.contains(e.target)) onSort(col);
                }
              : undefined
          }
          className={sortable ? 'cursor-pointer hover:underline flex-1' : 'flex-1'}
        >
          {children}
        </span>
        {filterable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFilterClick?.();
            }}
            className={[
              'shrink-0 inline-flex items-center justify-center w-4 h-4 rounded border transition-all',
              filterActive
                ? 'bg-gat-green text-white border-gat-greenDark shadow-sm'
                : 'bg-white text-gat-greenDark border-[#C0CCB4] hover:bg-gat-green hover:text-white hover:border-gat-greenDark',
            ].join(' ')}
            aria-label="Filter column"
            title={filterActive ? 'Filter active - click to edit' : 'Filter / sort this column'}
          >
            <svg width="8" height="8" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 01.78 1.625L12 11.27V16a1 1 0 01-1.45.894l-2-1A1 1 0 018 15v-3.73L3.22 4.625A1 1 0 013 4z" />
            </svg>
          </button>
        )}
      </div>
      {onResize && <ResizeGrip label={resizeLabel || col} onResize={onResize} onResizeReset={onResizeReset} />}
    </th>
  );
}

export function Td({ num, className = '', title, style, children, pad = 'px-1.5 py-1', hl }) {
  return (
    <td
      title={title}
      style={style}
      data-hl={hl ? '' : undefined}
      className={`${pad} text-text-dark font-medium ${num ? 'text-right tabular-nums' : ''} ${className}`}
    >
      {children}
    </td>
  );
}

// Plain render helper (not a component type) so the popover element tree is
// identical to the previous inline JSX - no remount/identity change.
export function renderFilterPopover({
  colId,
  columnLabel,
  isDate = false,
  isNumeric = false,
  isPercent = false,
  fv,
  values,
  setColumnFilters,
  sort,
  setSort,
  onClose,
}) {
  if (isDate) {
    return (
      <DateFilterPopover
        columnLabel={columnLabel}
        selected={fv || null}
        onApply={(range) => setColumnFilters((cur) => ({ ...cur, [colId]: range }))}
        sortDir={sort.col === colId ? sort.dir : null}
        onSort={(dir) => setSort({ col: colId, dir })}
        onClose={onClose}
      />
    );
  }
  if (isNumeric) {
    return (
      <NumericFilterPopover
        columnLabel={columnLabel}
        selected={fv || null}
        kind={isPercent ? 'percent' : 'money'}
        onApply={(range) => setColumnFilters((cur) => ({ ...cur, [colId]: range }))}
        sortDir={sort.col === colId ? sort.dir : null}
        onSort={(dir) => setSort({ col: colId, dir })}
        onClose={onClose}
      />
    );
  }
  return (
    <ColumnFilterPopover
      columnLabel={columnLabel}
      values={values || []}
      selected={fv}
      onApply={(s) => setColumnFilters((cur) => ({ ...cur, [colId]: s }))}
      sortDir={sort.col === colId ? sort.dir : null}
      onSort={(dir) => setSort({ col: colId, dir })}
      onClose={onClose}
    />
  );
}
